import { useCallback } from "react";

// Updated DynamicTable component with better handling
const DynamicTable = ({ formData, setFormData, tableConfig }) => {
  // Fix: Handle both array and object structures for items
  const getItemsArray = () => {
    if (!formData.items) return [];

    // If items is already an array, return it
    if (Array.isArray(formData.items)) {
      return formData.items;
    }

    // If items is an object, check for common array properties
    if (formData.items.list && Array.isArray(formData.items.list)) {
      return formData.items.list;
    }

    if (formData.items.documents && Array.isArray(formData.items.documents)) {
      return formData.items.documents;
    }

    // If items is an object but doesn't have array properties, return empty array
    return [];
  };

  const setItemsArray = (newItems) => {
    setFormData((prev) => {
      // If items was originally an array, keep it as an array
      if (Array.isArray(prev.items)) {
        return { ...prev, items: newItems };
      }

      // If items was an object, preserve the structure
      if (prev.items && typeof prev.items === "object") {
        if (prev.items.list !== undefined) {
          return { ...prev, items: { ...prev.items, list: newItems } };
        }
        if (prev.items.documents !== undefined) {
          return { ...prev, items: { ...prev.items, documents: newItems } };
        }
      }

      // Default: set as array
      return { ...prev, items: newItems };
    });
  };

  const handleItemChange = useCallback(
    (index, field, value) => {
      const currentItems = getItemsArray();
      const updatedItems = [...currentItems];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      setItemsArray(updatedItems);
    },
    [formData.items, setFormData]
  );

  const addItem = () => {
    const newItem = {};

    tableConfig.customColumns.forEach((col) => {
      newItem[col.key] =
        col.type === "number" || col.type === "currency"
          ? col.type === "number"
            ? 1
            : 0
          : "";
    });

    const currentItems = getItemsArray();
    setItemsArray([...currentItems, newItem]);
  };

  const removeItem = (index) => {
    const currentItems = getItemsArray();
    const updatedItems = currentItems.filter((_, i) => i !== index);
    setItemsArray(updatedItems);
  };

  const renderInputField = (item, column, index) => {
    const value =
      item[column.key] !== undefined && item[column.key] !== null
        ? item[column.key]
        : "";

    switch (column.type) {
      case "currency":
        return (
          <div className="currency-input-wrapper">
            <span className="currency-symbol">₱</span>
            <input
              type="text"
              inputMode="decimal"
              value={value === 0 ? "" : value}
              onChange={(e) => {
                let inputValue = e.target.value;

                // Allow empty, digits, and one decimal point
                if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
                  if (
                    inputValue.length > 1 &&
                    inputValue[0] === "0" &&
                    inputValue[1] !== "."
                  ) {
                    inputValue = inputValue.replace(/^0+/, "");
                  }

                  const numValue =
                    inputValue === "" ? 0 : parseFloat(inputValue) || 0;
                  handleItemChange(
                    index,
                    column.key,
                    inputValue === "" ? 0 : numValue
                  );
                }
              }}
              onBlur={(e) => {
                const numValue = parseFloat(e.target.value) || 0;
                handleItemChange(index, column.key, numValue);
              }}
              placeholder={column.header}
              className="currency-input"
            />
          </div>
        );

      case "number":
        return (
          <input
            type="number"
            min="0"
            value={value}
            onChange={(e) =>
              handleItemChange(index, column.key, e.target.value)
            }
            placeholder={column.header}
            className="w-full text-center custom-input"
          />
        );

      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) =>
              handleItemChange(index, column.key, e.target.value)
            }
            placeholder={column.header}
            className="w-full custom-input resize-none overflow-hidden"
            rows="1"
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={value}
            onChange={(e) =>
              handleItemChange(index, column.key, e.target.value)
            }
            className="w-full custom-input"
          />
        );

      default: // text
        return (
          <input
            type="text"
            value={value}
            onChange={(e) =>
              handleItemChange(index, column.key, e.target.value)
            }
            placeholder={column.header}
            className="w-full custom-input"
          />
        );
    }
  };

  // Fix: Use formData.tableIncluded as the source of truth instead of tableConfig
  const isTableIncluded = formData.tableIncluded === true;

  // Don't render if table is not included
  if (!isTableIncluded) {
    return null;
  }

  // Get the current items array
  const currentItems = getItemsArray();

  // Don't render if no items exist
  if (currentItems.length === 0) {
    return (
      <div className="m-4 p-4 bg-white/10 rounded-lg border border-alt-color text-center">
        <p className="text-sm text-gray-400 mb-4">No items added yet</p>
        <button type="button" onClick={addItem} className="btn-action">
          Add First Item
        </button>
      </div>
    );
  }

  return (
    <div className="m-4 bg-white/10 rounded-lg shadow-sm border border-alt-color dark:border-gray-600 overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="min-w-full table-fixed border-gray-400 border-seperate border">
          <thead className="bg-tier-secondary text-tier-primary backdrop-blur-md">
            <tr className="text-xs uppercase font-semibold tracking-wide">
              {tableConfig.customColumns.map((column, index) => (
                <th key={column.key} className="table-custom-th-form">
                  {column.header}
                </th>
              ))}
              <th className="table-custom-th-form w-16"></th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, index) => (
              <tr key={index} className="border-t">
                {tableConfig.customColumns.map((column) => (
                  <td
                    key={column.key}
                    className="px-1 py-2 table-custom-td-form"
                  >
                    {renderInputField(item, column, index)}
                  </td>
                ))}
                <td className="px-4 py-2 table-custom-td-form">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="action-btn-tb action-btn-delete"
                  >
                    <span className="material-symbols-outlined text-base">
                      close
                    </span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={addItem} className="m-2 btn-action">
        Add Item
      </button>
    </div>
  );
};

export default DynamicTable;
